
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

// Largest denominator a row's widths keep exactly (as fractions); rows needing a finer one are split in whole percent.
const PROPORTIONAL_MAX_DENOMINATOR = 24;

class ResponsiveUIAdjustmentProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict = pFable;
		this.defaultColumnCount = 12;
		// NOTE: The default column count is set to 12, which is the standard for Bulma CSS framework.
		// This can be adjusted based on the application's requirements, but Bulma supports up to 12 columns by default, so we'll need to expand the Bulma styling collection if we want to support more than that.
		this.activeColumnCount = this.defaultColumnCount;

		/** @type {(pInput: any) => boolean} */
		this._layoutExcludedInputCheck = (pInput) => this.isProportionalRowWidthMode() && this.isLayoutExcludedColumn(pInput);
		this.registerLayoutExcludedInputCheck();

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('Headlight-ProportionalColumns-CSS', this.getProportionalColumnCSS(), 500);
		}
	}

	/**
	 * Proportional row widths apply to the document forms (Dynamic Form / Materials Form), which carry a default
	 * form manifest; other apps built on this bundle keep the legacy per-field `is-N` classes.
	 *
	 * @return {Boolean}
	 */
	isProportionalRowWidthMode()
	{
		return Boolean(this.pict && this.pict.settings && this.pict.settings.MyDefaultFormManifest && this.pict.settings.MyDefaultFormManifest.Descriptors);
	}

	/**
	 * Hand isLayoutExcludedColumn to pict-section-form's Record layout, so its row widths leave hidden and
	 * zero-width inputs out. Idempotent; a no-op when the layout provider or its hook is missing.
	 */
	registerLayoutExcludedInputCheck()
	{
		const tmpRecordLayout = this.pict && this.pict.providers && this.pict.providers['Pict-Layout-Record'];
		if (tmpRecordLayout && (typeof tmpRecordLayout.registerLayoutExcludedInputCheck === 'function'))
		{
			tmpRecordLayout.registerLayoutExcludedInputCheck(this._layoutExcludedInputCheck);
		}
	}

	/**
	 * The share of its row an input takes, from pict-section-form's Record layout (its row grouping, raw widths
	 * and offsets, with layout-excluded inputs taking none).
	 *
	 * @param {Object} pInput - an input descriptor, as handed to {~ColumnWidth:~}
	 * @return {{Auto: boolean, Excluded: boolean, Width: RowShare|null, Offset: RowShare|null, Classes: string}|null} -
	 *   null when the legacy per-field classes apply (not a document form, or no Record-layout row for the input);
	 *   Auto when no visible input in the row sets a Width; otherwise the input's shares of the row and the
	 *   classes that size it
	 */
	getProportionalRowWidths(pInput)
	{
		if (!this.isProportionalRowWidthMode() || !pInput || !pInput.PictForm)
		{
			return null;
		}
		const tmpPictForm = pInput.PictForm;
		const tmpView = this.pict.views[tmpPictForm.ViewHash];
		if (!tmpView || (typeof tmpView.getRow !== 'function') || isNaN(tmpPictForm.GroupIndex) || isNaN(tmpPictForm.RowIndex))
		{
			return null;
		}
		const tmpRow = tmpView.getRow(tmpPictForm.GroupIndex, tmpPictForm.RowIndex);
		if (!tmpRow || !Array.isArray(tmpRow.Inputs) || !tmpRow.Inputs.includes(pInput) || !(tmpRow.WidthTotalRaw > 0))
		{
			return null;
		}
		const tmpRowHasWidth = tmpRow.Inputs.some((pRowInput) => pRowInput.PictForm && pRowInput.PictForm.Width && !pRowInput.PictForm.LayoutExcluded);
		if (!tmpRowHasWidth)
		{
			return { Auto: true, Excluded: false, Width: null, Offset: null, Classes: '' };
		}
		if (tmpPictForm.LayoutExcluded)
		{
			return { Auto: false, Excluded: true, Width: null, Offset: null, Classes: '' };
		}
		const tmpShare = this.allocateRowShares(tmpRow)[tmpRow.Inputs.indexOf(pInput)];
		const tmpClasses = [];
		if (tmpShare.Width)
		{
			tmpClasses.push(`hl-col-${tmpShare.Width.Numerator}-${tmpShare.Width.Denominator}`);
		}
		if (tmpShare.Offset)
		{
			tmpClasses.push(`hl-col-offset-${tmpShare.Offset.Numerator}-${tmpShare.Offset.Denominator}`);
		}
		return { Auto: false, Excluded: false, Width: tmpShare.Width, Offset: tmpShare.Offset, Classes: tmpClasses.join(' ') };
	}

	/**
	 * @typedef {Object} RowShare
	 * @property {number} Numerator
	 * @property {number} Denominator - reduced; at most PROPORTIONAL_MAX_DENOMINATOR, or 100 for a whole-percent share
	 */

	/**
	 * Each row input's width and offset as a reduced fraction of a pict-section-form row. The fractions are exact
	 * when every one reduces to a denominator of PROPORTIONAL_MAX_DENOMINATOR or less (thirds stay thirds);
	 * otherwise the row is split in whole percent by largest remainder. Either way the row totals exactly 1.
	 *
	 * @param {{Inputs: Array<Object>}} pRow - a Record-layout row after template generation
	 * @return {Array<{Width: RowShare|null, Offset: RowShare|null}>} - one entry per row input, in row order; null for a zero share
	 */
	allocateRowShares(pRow)
	{
		const fGCD = (pA, pB) => (pB ? fGCD(pB, pA % pB) : pA);
		const fReduce = (pNumerator, pDenominator) =>
		{
			if (!pNumerator)
			{
				return null;
			}
			const tmpDivisor = fGCD(pNumerator, pDenominator);
			return { Numerator: pNumerator / tmpDivisor, Denominator: pDenominator / tmpDivisor };
		};
		const tmpParts = [];
		pRow.Inputs.forEach((pRowInput, pIndex) =>
		{
			const tmpPictForm = pRowInput.PictForm || {};
			tmpParts.push({ Index: pIndex, Key: 'Width', Raw: tmpPictForm.RawWidth || 0 });
			tmpParts.push({ Index: pIndex, Key: 'Offset', Raw: tmpPictForm.RawWidthOffset || 0 });
		});
		const tmpResult = pRow.Inputs.map(() => ({ Width: null, Offset: null }));

		const tmpScale = [ 1, 2, 4, 5, 10, 100 ].find((pScale) => tmpParts.every((pPart) => Math.abs((pPart.Raw * pScale) - Math.round(pPart.Raw * pScale)) < 1e-9));
		if (tmpScale)
		{
			const tmpTotal = tmpParts.reduce((pSum, pPart) => pSum + Math.round(pPart.Raw * tmpScale), 0);
			const tmpExact = tmpParts.map((pPart) => fReduce(Math.round(pPart.Raw * tmpScale), tmpTotal));
			if ((tmpTotal > 0) && tmpExact.every((pShare) => !pShare || (pShare.Denominator <= PROPORTIONAL_MAX_DENOMINATOR)))
			{
				tmpParts.forEach((pPart, pIndex) =>
				{
					tmpResult[pPart.Index][pPart.Key] = tmpExact[pIndex];
				});
				return tmpResult;
			}
		}

		const tmpRawTotal = tmpParts.reduce((pSum, pPart) => pSum + pPart.Raw, 0);
		let tmpAllocated = 0;
		for (const tmpPart of tmpParts)
		{
			tmpPart.Exact = tmpPart.Raw / tmpRawTotal * 100;
			tmpPart.Percent = Math.floor(tmpPart.Exact);
			tmpAllocated += tmpPart.Percent;
		}
		const tmpByRemainder = tmpParts.filter((pPart) => pPart.Exact > pPart.Percent).sort((pA, pB) => (pB.Exact - pB.Percent) - (pA.Exact - pA.Percent));
		for (let i = 0; (i < tmpByRemainder.length) && (tmpAllocated < 100); i++)
		{
			tmpByRemainder[i].Percent++;
			tmpAllocated++;
		}
		for (const tmpPart of tmpParts)
		{
			tmpResult[tmpPart.Index][tmpPart.Key] = fReduce(tmpPart.Percent, 100);
		}
		return tmpResult;
	}

	/**
	 * The stylesheet behind the classes {~ColumnWidth:~} emits for proportional rows: `hl-col-N-D` (width) and
	 * `hl-col-offset-N-D` for every reduced fraction with a denominator up to PROPORTIONAL_MAX_DENOMINATOR, and
	 * every whole percent, inside Bulma's own column breakpoint so rows still stack on mobile.
	 *
	 * @return {string}
	 */
	getProportionalColumnCSS()
	{
		const fGCD = (pA, pB) => (pB ? fGCD(pB, pA % pB) : pA);
		const tmpFractions = new Map();
		const tmpDenominators = [];
		for (let d = 1; d <= PROPORTIONAL_MAX_DENOMINATOR; d++)
		{
			tmpDenominators.push(d);
		}
		tmpDenominators.push(100);
		for (const tmpDenominator of tmpDenominators)
		{
			for (let n = 1; n <= tmpDenominator; n++)
			{
				const tmpDivisor = fGCD(n, tmpDenominator);
				tmpFractions.set(`${n / tmpDivisor}-${tmpDenominator / tmpDivisor}`, Number((n / tmpDenominator * 100).toFixed(4)));
			}
		}
		const tmpRules = [];
		for (const [ tmpKey, tmpPercent ] of tmpFractions)
		{
			tmpRules.push(`.columns > .column.hl-col-${tmpKey} { flex: none; width: ${tmpPercent}%; }`);
			tmpRules.push(`.columns > .column.hl-col-offset-${tmpKey} { margin-inline-start: ${tmpPercent}%; }`);
		}
		return `@media screen and (min-width: 769px), print {\n${tmpRules.join('\n')}\n}\n`;
	}

	/**
	 * Set the active column count for responsive UI adjustments
	 * @param {Number} pColumnCount - The number of columns to set
	 * @return {Number} - The active column count after setting
	 */
	setActiveColumnCount(pColumnCount)
	{
		if (typeof pColumnCount !== 'number' || pColumnCount <= 0)
		{
			this.pict.log.error('Invalid column count provided.');
			throw new Error('Invalid column count provided.');
		}
		// if the column count is greater than the default, we need to adjust it to the default until we can expand the Bulma styling collection
		if (pColumnCount > this.defaultColumnCount)
		{
			this.pict.log.warn(`Requested column count ${pColumnCount} exceeds default column count ${this.defaultColumnCount}. Adjusting to default.`);
			this.pict.log.warn(`DEVELOPER NOTE: Bulma styling collection will need to be expanded to support more than ${this.defaultColumnCount} columns.`);
			pColumnCount = this.defaultColumnCount;
		}
		this.activeColumnCount = pColumnCount;
		return this.activeColumnCount;
	}

	/**
	 * Get the current active column count for responsive UI adjustments
	 * @return {Number} - The current active column count
	 */
	getActiveColumnCount()
	{
		return this.activeColumnCount;
	}

	/**
	 * Reset the active column count to the default value
	 * @return {Number} - The active column count after reset
	 */
	resetActiveColumnCount()
	{
		this.activeColumnCount = this.defaultColumnCount;
		return this.activeColumnCount;
	}

	/**
	 * Determine whether a column should be excluded from the row layout math.
	 *
	 * An excluded column is layout-neutral: it is dropped from every width sum / normalization
	 * so the remaining visible columns fill the row as if it were not present, and the
	 * {~ColumnWidth:~} macro renders it taking no horizontal space.
	 *
	 * A column is excluded when ANY of:
	 *   - its PictForm.Width is explicitly zero (parseFloat === 0) — the deliberate "no space" signal,
	 *   - it is a hidden input type (InputType === 'Hidden' or starting with 'MaterialsHidden'), OR
	 *   - it carries a truthy PictForm.Hidden flag (the Materials input types honor this to hide a field).
	 *
	 * A column with NO Width set (undefined/null/'') is NOT excluded on the width axis — it is a
	 * normal visible column that defaults to a width of 1 elsewhere. Only an explicit zero counts.
	 *
	 * @param {Object} pColumn - The column descriptor to test
	 * @return {Boolean} - Returns true when the column should not occupy layout space
	 */
	isLayoutExcludedColumn(pColumn)
	{
		if (!pColumn || !pColumn.PictForm)
		{
			return false;
		}
		const tmpInputType = pColumn.PictForm.InputType;
		if (typeof tmpInputType === 'string' && (tmpInputType === 'Hidden' || tmpInputType.startsWith('MaterialsHidden')))
		{
			return true;
		}
		// The Materials input types honor an explicit PictForm.Hidden flag to hide a field; treat it as layout-excluded too.
		if (pColumn.PictForm.Hidden)
		{
			return true;
		}
		// Distinguish an explicit zero Width (excluded) from an unset Width (a normal, visible un-widthed column).
		const tmpWidth = pColumn.PictForm.Width;
		if (tmpWidth !== undefined && tmpWidth !== null && tmpWidth !== '' && parseFloat(tmpWidth) === 0)
		{
			return true;
		}
		return false;
	}

	/**
	 * Check if the row has any columns with a PictForm.Width set and if they exceed the active column count
	 * @param {Object[]} pRowObject - The Pict row object containing columns to check
	 * @return {Boolean} - Returns true if the row has columns exceeding the active column count, false otherwise
	 */
	checkForOvercrowdingInRow(pRowObject)
	{
		// Check if the row has any columns with a PictForm.Width set and if they exceed the active column count
		// Layout-excluded columns (explicit zero width / hidden inputs) take no space, so they are not counted.
		const totalWidth = pRowObject.reduce((acc, column) =>
		{
			if (this.isLayoutExcludedColumn(column))
			{
				return acc;
			}
			return acc + (column.PictForm.Width ? parseFloat(column.PictForm.Width) : 1);
		}, 0);
		if (totalWidth > this.activeColumnCount)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Row columns exceed active column count of ${this.activeColumnCount}. Will adjust columns accordingly.`);
			}
			return true;
		}
		return false;
	}

	/**
	 * Map all rows by their sections and mark each group holding a row whose widths overrun the active column count
	 * @return {Boolean} - Returns true if the mapping was successful
	 */
	mapAllRowsBySections(pDescriptors, pSections)
	{
		if (!pDescriptors || typeof pDescriptors !== 'object')
		{
			this.pict.log.error('Invalid descriptors provided for mapping rows by sections.');
			throw new Error('Invalid descriptors provided for mapping rows by sections.');
		}
		const tmpColumns = pDescriptors;
		// map the column by their Section and Row
		const tmpRowsBySection = {};
		Object.values(tmpColumns).forEach((column) =>
		{
			if (!column.PictForm || !column.PictForm.Section)
			{
				if (this.pict.LogNoisiness > 2)
				{
					this.pict.log.warn(`Column ${column.Name} does not have a valid Section defined. Skipping.`);
				}
				return;
			}
			const tmpRowOrdinal = column.PictForm.Row || '1'; // default to row 1 if not defined
			// Objects, not arrays: rows are keyed by Row as written ("1.5", "8.0"), which are not array indexes.
			if (!tmpRowsBySection[column.PictForm.Section])
			{
				tmpRowsBySection[column.PictForm.Section] = {};
			}
			if (!tmpRowsBySection[column.PictForm.Section][column.PictForm.Group])
			{
				tmpRowsBySection[column.PictForm.Section][column.PictForm.Group] = {};
			}
			if (!tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal])
			{
				tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal] = [];
			}
			tmpRowsBySection[column.PictForm.Section][column.PictForm.Group][tmpRowOrdinal].push(column);
		});
		// now we need to adjust the columns for each row based on the active column count
		for (const section in tmpRowsBySection)
		{
			if (Object.prototype.hasOwnProperty.call(tmpRowsBySection, section))
			{
				// now we need to iterate over each row in the section and adjust the columns based on the active column count
				if (!tmpRowsBySection[section] || !Object.keys(tmpRowsBySection[section]).length)
				{
					if (this.pict.LogNoisiness > 2)
					{
						this.pict.log.warn(`Section ${section} does not have any rows defined. Skipping adjustment.`);
					}
					continue;
				}

				for (const group in tmpRowsBySection[section])
				{
					if (Object.prototype.hasOwnProperty.call(tmpRowsBySection[section], group))
					{
						const groupObject = tmpRowsBySection[section][group];

						for (const row in groupObject)
						{
							if (Object.prototype.hasOwnProperty.call(groupObject, row))
							{
								const rowObject = groupObject[row];
								// check if the row overcrowded before we do any adjustments, so we can update the parent "columns" wrapper
								if (this.checkForOvercrowdingInRow(rowObject))
								{
									if (this.pict.LogNoisiness > 2)
									{
										this.pict.log.warn(`Row ${row} in section ${section} is overcrowded. Adjusting columns.`);
									}
									// NOTE: we mark the entire group as overcrowded, so we can adjust the parent "columns" wrappers
									// This doesn't negatively impact any non-overcrowded rows, as they will be balanced with Bulma classes, however,
									// it would be better to mark the row itself as overcrowded, but that would require a change in the PictForm structure
									this.markGroupAsOvercrowded(rowObject, pSections, section, group);
								}
							}
						}
						// now we need to set the adjusted rows back to the section
						tmpRowsBySection[section][group] = Object.values(groupObject).flat();
					}
				}
				// now we need to set the adjusted rows back to the section
				tmpRowsBySection[section] = Object.values(tmpRowsBySection[section]).map((group) =>
				{
					return Object.values(group).flat();
				});
			}
		}
		// temp log the adjusted rows by section
		if (this.pict.LogNoisiness > 2)
		{
			this.pict.log.info('Adjusted rows by section:', tmpRowsBySection);
		}
		return true;
	}

	/**
	 * Mark the row as overcrowded by adding a PictForm property
	 * @param {Object[]} pRowObject - The Pict row object containing columns to mark as overcrowded
	 * @param {Object} pSectionsObject - The Pict sections object containing the section information
	 * @param {String} pSectionName - The name of the section containing the row
	 * @param {String} pGroupName - The name of the group containing the row
	 * @return {Object} - The modified group object with overcrowded columns marked
	 */
	markGroupAsOvercrowded(pRowObject, pSectionsObject, pSectionName, pGroupName)
	{
		// mark the row as overcrowded by adding a PictForm property
		if (!pRowObject || !Array.isArray(pRowObject))
		{
			this.pict.log.error('Invalid row object provided for marking as overcrowded.');
			throw new Error('Invalid row object provided for marking as overcrowded.');
		}
		if (!pSectionsObject || typeof pSectionsObject !== 'object')
		{
			this.pict.log.error('Invalid sections object provided for marking as overcrowded.');
			throw new Error('Invalid sections object provided for marking as overcrowded.');
		}
		if (!pSectionName || typeof pSectionName !== 'string')
		{
			this.pict.log.error('Invalid section name provided for marking as overcrowded.');
			pSectionName = 'UNSET_SECTION'; // fallback to default section name if not provided
		}
		if (!pGroupName || typeof pGroupName !== 'string')
		{
			this.pict.log.error('Invalid group name provided for marking as overcrowded.');
			pGroupName = 'UNSET_GROUP'; // fallback to default group name if not provided
		}
		// find the section and group in the sections object
		const tmpSection = pSectionsObject.find((section) => section.Hash === pSectionName);
		if (!tmpSection)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Section ${pSectionName} not found in sections object.`);
			}
			return {};
		}
		const pGroupObject = tmpSection.Groups.find((group) => group.Hash === pGroupName);
		if (!pGroupObject)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.warn(`Group ${pGroupName} not found in section ${pSectionName}.`);
			}
			return {};
		}
		// now we need to mark the row as overcrowded by adding a PictForm property
		if (!pGroupObject._OvercrowdedColumns)
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info(`Marking group ${pGroupName} in section ${pSectionName} as overcrowded.`);
			}
			pGroupObject._OvercrowdedColumns = true;
		}
		else
		{
			if (this.pict.LogNoisiness > 2)
			{
				this.pict.log.info(`Group ${pGroupName} in section ${pSectionName} is already marked as overcrowded.`);
			}
		}
		// check if the row is overcrowded and set the _OvercrowdedColumns property
		return pGroupObject;
	}

	/**
	 * Called after the provider is initialized, this method will adjust the UI based on the active column count
	 * @param {Function} [pCallback] - Optional callback to execute after initialization
	 * @return {Promise} - A promise that resolves when the initialization is complete
	 * @async
	 */
	onInitializeAsync(pCallback)
	{
		// don't break if sub-dynamic forms are using the correct pattern yet
		if (!this.pict || !this.pict.settings || !this.pict.settings.MyDefaultFormManifest || !this.pict.settings.MyDefaultFormManifest.Descriptors)
		{
			this.pict.log.warn('Pict settings or descriptors are not properly initialized to run ResponsiveUIAdjustmentProvider.');
			// if the callback is provided, we can call it to continue the initialization process
			if (typeof pCallback === 'function')
			{
				return pCallback();
			}
			return Promise.resolve();
		}
		// set the active column count based on the Pict settings, if available
		this.pict.log.info('ResponsiveUIAdjustmentProvider initialized with active column count:', this.activeColumnCount);
		this.registerLayoutExcludedInputCheck();
		// column widths come from getProportionalRowWidths; this pass only marks overcrowded groups
		this.mapAllRowsBySections(this.pict.settings?.MyDefaultFormManifest?.Descriptors, this.pict.settings?.MyDefaultFormManifest?.Sections);

		// add a resize event listener to the window to adjust the UI elements when the window is resized
		const resizeDebounce = 100; // debounce time in milliseconds
		let resizeTimeout;
		// NOTE: We use a debounce to prevent excessive calls to the resize handler, which can be expensive in terms of performance
		window.addEventListener('resize', () =>
		{
			if (resizeTimeout)
			{
				clearTimeout(resizeTimeout);
			}
			resizeTimeout = setTimeout(() =>
			{
				this.onWindowResize();
			}, resizeDebounce);
		});
		this.pict.log.info('ResponsiveUIAdjustmentProvider: Window resize event listener added.');
		// also, let's run it once to adjust the UI elements immediately
		this.onWindowResize();

		if (typeof pCallback === 'function')
		{
			return pCallback();
		}
		return Promise.resolve();
	}

	/**
	 * Called when the window is resized, this method will adjust all the labels to be a consistent height across all columns in the row
	 * @return {void}
	 */
	onWindowResize()
	{
		// go through all sections and groups, and adjust the label heights to be consistent for all columns in the row
		this.pict.log.info('ResponsiveUIAdjustmentProvider: Window resized, adjusting UI elements.');
		const allColumnRows = document.querySelectorAll('.columns:not(.is-hidden)');
		allColumnRows.forEach((row) =>
		{
			const columns = row.querySelectorAll('.column');
			if (columns.length > 0)
			{
				let maxHeight = 0;
				// find the maximum height of the labels in the columns
				columns.forEach((column) =>
				{
					const label = column.querySelector('label');
					if (label && label.offsetHeight > maxHeight)
					{
						maxHeight = label.offsetHeight;
					}
				});
				// if the maximum height is greater than 0, we can set the height of all labels to the maximum height
				if (maxHeight > 0)
				{
					columns.forEach((column) =>
					{
						const label = column.querySelector('label');
						if (label)
						{
							label.style.height = `${maxHeight}px`;
						}
					});
				}
			}
		});
		// A group can cross the "big enough to warrant an inline/modal choice"
		// threshold purely because the window got shorter, so ride this already
		// debounced resize pass rather than registering another listener.
		// NOTE: refreshEntryModeToggleVisibility must never call back into
		// prepareAnyExtraTableControls -- that would recurse through here.
		const tmpTableControls = this.pict.providers['Bulma-TableControls'];
		if (tmpTableControls && (typeof tmpTableControls.refreshEntryModeToggleVisibility === 'function'))
		{
			tmpTableControls.refreshEntryModeToggleVisibility();
		}
	}
}
module.exports = ResponsiveUIAdjustmentProvider;
